// The trades' market (B-388): the league's block, the draft picks that have
// changed hands where the league trades them, from Standard every roster's
// share of the top players at each position, and at Everything the teams
// that fit mine. Beside the offers on a desktop; the Market segment on a
// tablet or a phone. Facts only: counts and names, never who to trade with
// (CLAUDE.md, tools not instructions).

import type { DraftPickRef, RosterDepthView, TeamRef, TradeBlockEntry } from '@fsp/domain';
import { useAtLeast } from '../../data/detail';
import { positionLabel, TeamMark } from '../../kit';
import { Panel } from '../../kit/Panel';
import { ListRow, RowName, RowSub, Text } from '../../kit/ui';
import { Term } from '../../kit/Why';
import { GLOSSARY } from '../shared/glossary';
import { TeamName } from '../shared/Names';
import { pickShort } from '../shared/pickLabel';
import { TradeBlockPanel } from '../shared/TradeBlock';
import { useTools } from '../../data/tools';

export interface MarketProps {
  block: TradeBlockEntry[];
  depth: RosterDepthView | null;
  /** Trading is open (deadline, playoffs). */
  open: boolean;
  /**
   * The league trades draft picks now, and those that have changed hands
   * (PLAN-2026-10-27-the-next-season §2.6). Absent: a host with none.
   */
  picksOpen?: boolean;
  picks?: DraftPickRef[];
  /** Open the builder with this team as the partner, and maybe a player of theirs. */
  onOffer: (teamId: string, playerId?: string) => void;
  onRemove: (playerId: string) => void;
  onMark: () => void;
}

/** The market's panels, in the order a manager reads them. */
export function TradesMarket({
  block,
  depth,
  open,
  picksOpen = false,
  picks = [],
  onOffer,
  onRemove,
  onMark,
}: MarketProps) {
  const standard = useAtLeast('standard');
  const pro = useAtLeast('pro');
  return (
    <>
      <Panel title="Trade block" className="fsp-tr-block-panel" bodyClass="fsp-tr-block">
        <TradeBlockPanel
          entries={block}
          open={open}
          onMark={onMark}
          onRemove={onRemove}
          onOffer={(e) => onOffer(e.team.id, e.player.id)}
        />
      </Panel>
      {picksOpen ? <TradedPicks picks={picks} open={open} onOffer={onOffer} /> : null}
      {pro && depth !== null ? <TradePartners depth={depth} open={open} onOffer={onOffer} /> : null}
      {standard && depth !== null ? (
        <RostersByPosition depth={depth} open={open} onOffer={onOffer} />
      ) : null}
    </>
  );
}

/**
 * THE DRAFT PICKS THAT HAVE CHANGED HANDS (§2.6), soonest draft first: whose
 * pick each is and who holds it now, as Sleeper's board shows them. A row
 * opens a trade with its holder. Names, so at every level.
 */
function TradedPicks({
  picks,
  open,
  onOffer,
}: {
  picks: DraftPickRef[];
  open: boolean;
  onOffer: (teamId: string) => void;
}) {
  return (
    <Panel title="Traded picks" className="fsp-tr-picks-panel">
      {picks.length === 0 ? (
        <Text tone="secondary" size="sm" block>
          No draft picks have changed hands yet.
        </Text>
      ) : (
        picks.map((p) => {
          const holder = p.holder;
          const theirs = holder !== null && !holder.isMine;
          return (
            <ListRow
              key={p.id}
              lead={holder !== null ? <TeamMark team={holder} size={22} /> : undefined}
              onClick={open && theirs ? () => onOffer(holder.id) : undefined}
              cover={open && theirs ? `Trade with ${holder.ownerName}` : undefined}
            >
              <RowName>{pickShort(p)}</RowName>
              <RowSub>
                {holder === null ? (
                  p.name
                ) : holder.isMine ? (
                  'Yours now'
                ) : (
                  <>
                    Held by <TeamName team={holder}>{holder.name}</TeamName>
                  </>
                )}
              </RowSub>
            </ListRow>
          );
        })
      )}
    </Panel>
  );
}

/** "RB", "RBs". */
function plural(position: string, n: number): string {
  return n === 1 ? position : `${position}s`;
}

/**
 * EVERY ROSTER'S SHARE OF THE TOP PLAYERS at each position (B-388): a count
 * a cell, green where a team has more than it starts and red where it has
 * fewer, so who is deep where shows at a glance. A team's mark opens a trade
 * with it. Numbers, so from Standard.
 */
export function RostersByPosition({
  depth,
  open,
  onOffer,
}: {
  depth: RosterDepthView;
  open: boolean;
  onOffer: (teamId: string) => void;
}) {
  const cols = depth.positions;
  return (
    <Panel
      title="Rosters by position"
      meta={
        <span className="fsp-muted fsp-sb-count">
          <Term tip={GLOSSARY.topPlayers}>Top players</Term>
        </span>
      }
    >
      <div className="fsp-tr-depth-wrap">
        <p className="fsp-tr-note fsp-muted">
          How many of the league’s top players at each position each team has. Green is more than it
          starts there, red fewer.
        </p>
        <table className="fsp-tr-depth">
          <thead>
            <tr>
              <th scope="col">
                <span className="fsp-sr">Team</span>
              </th>
              {cols.map((c) => (
                <th key={c.position} scope="col">
                  <span className="fsp-tr-pos">{positionLabel(c.position)}</span>
                  <span className="fsp-tr-starts">starts {c.slots}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {depth.teams.map(({ team, held }) => (
              <tr key={team.id} aria-current={team.isMine || undefined}>
                <th scope="row">
                  <TeamCell team={team} open={open} onOffer={onOffer} />
                </th>
                {cols.map((c) => {
                  const n = held.find((h) => h.position === c.position)?.count ?? 0;
                  const state = n > c.slots ? 'deep' : n < c.slots ? 'thin' : 'even';
                  return (
                    <td
                      key={c.position}
                      className="fsp-num"
                      data-depth={state}
                      aria-label={`${team.name}: ${n} of the top ${c.tier} ${plural(positionLabel(c.position), c.tier)}, starts ${c.slots}`}
                    >
                      {n}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/** A row's team: its mark, and a door into a trade with it unless it is mine. */
function TeamCell({
  team,
  open,
  onOffer,
}: {
  team: TeamRef;
  open: boolean;
  onOffer: (teamId: string) => void;
}) {
  const inner = (
    <>
      <TeamMark team={team} size={20} />
      <span className="fsp-tr-abbr">{team.abbreviation}</span>
    </>
  );
  if (team.isMine || !open) return <span className="fsp-tr-team">{inner}</span>;
  return (
    <button
      type="button"
      className="fsp-tr-team"
      onClick={() => onOffer(team.id)}
      // THE LETTERS IT SHOWS COME FIRST in what it is called, so a voice
      // command saying them finds it (WCAG 2.5.3; the UX run of 2026-10-21).
      aria-label={`${team.abbreviation}: start a trade with ${team.name}`}
      title={team.name}
    >
      {inner}
    </button>
  );
}

/**
 * THE TEAMS THAT FIT MINE (TRADES.md Pro mode, B-388): deep where I am thin,
 * and first those also thin where I am deep, each with the counts that
 * matched it, so the manager sees the logic and can disagree with it. A
 * shortlist of teams, never an offer. At Everything.
 */
export function TradePartners({
  depth,
  open,
  onOffer,
}: {
  depth: RosterDepthView;
  open: boolean;
  onOffer: (teamId: string) => void;
}) {
  const tools = useTools();
  const me = depth.teams.find((t) => t.team.isMine) ?? null;
  // The shortlist is a suggestion: none in a league that turned them off
  // (RULES_SCHEMA §15c), where its empty list would read as no team fitting.
  if (me === null || !tools.suggestions) return null;
  const count = (teamId: string, position: string) =>
    depth.teams.find((t) => t.team.id === teamId)?.held.find((h) => h.position === position)
      ?.count ?? 0;
  // "RB 3 to your 1", "WR 4 to their 1": the counts that matched, so the
  // manager can check them against the rosters by position.
  const pairs = (teamId: string, positions: string[], theirsFirst: boolean) =>
    positions
      .map((p) => {
        const theirs = count(teamId, p);
        const mine = count(me.team.id, p);
        return theirsFirst ? `${p} ${theirs} to your ${mine}` : `${p} ${mine} to their ${theirs}`;
      })
      .join(', ');
  return (
    <Panel
      title="Trade partners"
      meta={
        <span className="fsp-muted fsp-sb-count">
          <Term tip={GLOSSARY.tradePartners}>Who fits</Term>
        </span>
      }
    >
      {depth.partners.length === 0 ? (
        <p className="fsp-tr-note fsp-muted">
          No team has more of the top players than it starts where you have fewer.
        </p>
      ) : (
        depth.partners.map((x) => {
          const team = depth.teams.find((t) => t.team.id === x.teamId)?.team;
          if (team === undefined) return null;
          return (
            <ListRow
              key={x.teamId}
              lead={<TeamMark team={team} size={24} />}
              tall
              onClick={open ? () => onOffer(team.id) : undefined}
              cover={`Start a trade with ${team.name}`}
            >
              <RowName>
                <TeamName team={team}>{team.name}</TeamName>
              </RowName>
              <RowSub wrap>
                <Text tone="good" size="xs">
                  They have more:
                </Text>{' '}
                {pairs(team.id, x.theyHaveMore, true)}
              </RowSub>
              {x.youHaveMore.length > 0 ? (
                <RowSub wrap>
                  <Text tone="accent" size="xs">
                    You have more:
                  </Text>{' '}
                  {pairs(team.id, x.youHaveMore, false)}
                </RowSub>
              ) : null}
            </ListRow>
          );
        })
      )}
    </Panel>
  );
}
